import { err, ok } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import type { ContentItem } from '@src/components/Home/contentItems';
import type { StoryRepository } from '@src/repositories/StoryRepository';
import type { Result } from '@src/services/common/Result';
import type { DashboardStory } from '@src/types.ts/story';

export interface ContentDeletionDeps {
  storyRepository: StoryRepository;
  /** Current homeStories for reading savedBlog/savedSocialPost data */
  getHomeStories: () => DashboardStory[];
}

export interface DeleteContentResult {
  deletedCount: number;
  errors: Array<{ id: string; error: string }>;
}

/**
 * Parses a composite content ID like "storyId-blog-timestamp" and
 * returns the timestamp portion, or null if the format is invalid.
 */
export function parseGeneratedContentTimestamp(
  contentId: string,
  prefix: 'blog' | 'social',
): string | null {
  const marker = `-${prefix}-`;
  const idx = contentId.indexOf(marker);
  if (idx === -1) return null;
  return contentId.slice(idx + marker.length);
}

const logger = LogManager.getLogger('service:ContentDeletionService');

export class ContentDeletionService {
  private readonly deps: ContentDeletionDeps;

  constructor(deps: ContentDeletionDeps) {
    this.deps = deps;
  }

  async deleteContentItems(
    items: ContentItem[],
  ): Promise<Result<DeleteContentResult>> {
    if (items.length === 0) {
      return err('VALIDATION', 'No items selected for deletion');
    }

    const errors: Array<{ id: string; error: string }> = [];
    let deletedCount = 0;

    // Group articles and social posts by storyId for batched updates
    const articlesByStory = new Map<string, ContentItem[]>();
    const socialByStory = new Map<string, ContentItem[]>();
    const quotesByStory = new Map<string, ContentItem[]>();

    for (const item of items) {
      if (item.contentType === 'article') {
        const group = articlesByStory.get(item.storyId) ?? [];
        group.push(item);
        articlesByStory.set(item.storyId, group);
      } else if (item.contentType === 'social') {
        const group = socialByStory.get(item.storyId) ?? [];
        group.push(item);
        socialByStory.set(item.storyId, group);
      } else if (item.contentType === 'quote') {
        const group = quotesByStory.get(item.storyId) ?? [];
        group.push(item);
        quotesByStory.set(item.storyId, group);
      }
    }

    // Delete clips
    for (const item of items.filter(i => i.contentType === 'clip')) {
      try {
        await this.deps.storyRepository.removeVideoFromStory(
          item.storyId,
          item.id,
        );
        deletedCount++;
      } catch (e) {
        logger.error('Failed to delete clip', {
          itemId: item.id,
          ...extractErrorInfo(e),
        });
        errors.push({ id: item.id, error: 'Failed to delete clip' });
      }
    }

    // Delete quotes (shareable images), batched by story. Unlinking from the
    // story is the delete — a raw `items.destroy` is a no-op through the
    // arbor-v1 Dato proxy; see `removeShareableImagesFromStory`.
    for (const [storyId, quoteItems] of quotesByStory) {
      try {
        await this.deps.storyRepository.removeShareableImagesFromStory(
          storyId,
          quoteItems.map(item => item.id),
        );
        deletedCount += quoteItems.length;
      } catch (e) {
        logger.error('Failed to delete quotes', {
          storyId,
          ...extractErrorInfo(e),
        });
        quoteItems.forEach(item =>
          errors.push({ id: item.id, error: 'Failed to delete quote' }),
        );
      }
    }

    // Delete articles (batched by story)
    for (const [storyId, articleItems] of articlesByStory) {
      try {
        await this.deleteGeneratedContent(
          storyId,
          articleItems,
          'blog',
          'savedBlog',
          'saved_blog',
        );
        deletedCount += articleItems.length;
      } catch (e) {
        logger.error('Failed to delete articles', {
          storyId,
          ...extractErrorInfo(e),
        });
        articleItems.forEach(item =>
          errors.push({ id: item.id, error: 'Failed to delete article' }),
        );
      }
    }

    // Delete social posts (batched by story)
    for (const [storyId, socialItems] of socialByStory) {
      try {
        await this.deleteGeneratedContent(
          storyId,
          socialItems,
          'social',
          'savedSocialPost',
          'saved_social_post',
        );
        deletedCount += socialItems.length;
      } catch (e) {
        logger.error('Failed to delete social posts', {
          storyId,
          ...extractErrorInfo(e),
        });
        socialItems.forEach(item =>
          errors.push({ id: item.id, error: 'Failed to delete social post' }),
        );
      }
    }

    return ok({ deletedCount, errors });
  }

  private async deleteGeneratedContent(
    storyId: string,
    items: ContentItem[],
    prefix: 'blog' | 'social',
    storyField: 'savedBlog' | 'savedSocialPost',
    saveType: 'saved_blog' | 'saved_social_post',
  ): Promise<void> {
    const story = this.deps.getHomeStories().find(s => s.id === storyId);
    if (!story) {
      throw new Error(`Story ${storyId} not found in homeStories`);
    }

    // Copy the saved data so we can mutate without affecting the store
    const savedData: Record<number, unknown> = story[storyField]
      ? { ...story[storyField] }
      : {};

    for (const item of items) {
      const timestamp = parseGeneratedContentTimestamp(item.id, prefix);
      if (timestamp) {
        delete savedData[Number(timestamp)];
      }
    }

    await this.deps.storyRepository.handleSaveAiGeneratedContent(
      storyId,
      savedData,
      saveType,
    );
  }
}
